'use client'

import { createContext, useContext, useSyncExternalStore } from 'react'
import { create } from 'zustand'
import { normalizeHabitIds } from '@/features/studio-renew/lib/character-habits'
import { hasWorkValues } from '@/features/studio-renew/lib/work-draft'

import type {
  Character,
  Chat,
  Command,
  QuestState,
  StatusBoard,
  StatusField,
  World,
} from '@/features/studio-renew/types'

/** 독립 API가 없는 편집 행의 키. 재료·작품 ID와 URL에는 사용하지 않는다. */
export const newRowId = () => crypto.randomUUID()

export type Kind = 'world' | 'character' | 'chat' | 'status' | 'command'
export type StudioData = Data
type Data = {
  worlds: Record<string, World>
  characters: Record<string, Character>
  chats: Record<string, Chat>
  statuses: Record<string, StatusBoard>
  commands: Record<string, Command>
  quest: QuestState
}
export type RenewStoreState = Data & {
  owner: string | null
  /** 마지막으로 저장된 시각 — 헤더 "저장됨 12:40" */
  savedAt: number | null
  setOwner: (owner: string | null) => void
  /** 서버에서 받은 재료 · 작품으로 현재 탭을 채운다 — sync.ts 의 loadStudio 만 부른다 */
  hydrate: (next: Omit<Data, 'quest'>) => void
  updateWorld: (id: string, patch: Partial<World>) => void
  updateCharacter: (id: string, patch: Partial<Character>) => void
  updateChat: (id: string, patch: Partial<Chat>) => void
  updateStatus: (id: string, patch: Partial<StatusBoard>) => void
  updateCommand: (id: string, patch: Partial<Command>) => void
  remove: (kind: Kind, id: string) => void
  setQuest: (patch: Partial<QuestState>) => void
}

/* 미입력 편집 판정. 서버가 만든 초안을 목록 진입만으로 삭제하지 않는다. */
const blankCharacterDraft = (c: Character) =>
  !c.name.trim() &&
  !c.image &&
  !c.avatar &&
  !c.tagline.trim() &&
  !c.persona.trim() &&
  !c.worldNote.trim() &&
  !c.worldId &&
  !c.habits.some((row) => row.text.trim()) &&
  !c.forbidden.some((text) => text.trim()) &&
  !c.album?.some((row) => row.image.trim() || row.situation?.trim())
const blankWorldDraft = (w: World) =>
  !w.name.trim() &&
  !w.summary.trim() &&
  !w.cover &&
  !w.body.trim() &&
  !w.keywords.some(
    (row) => row.text.trim() || row.keys.some((key) => key.trim())
  ) &&
  !w.places.some((row) => row.name.trim() || row.note.trim())
const blankStatusDraft = (s: StatusBoard) =>
  !s.name.trim() &&
  !s.html.trim() &&
  !s.fields.some(
    (row) =>
      !row.hidden &&
      (row.key.trim() ||
        row.initial.trim() ||
        row.rule.trim() ||
        row.options.some((value) => value.trim()))
  )
const blankCommandDraft = (c: Command) =>
  c.key.length <= 1 &&
  !c.title.trim() &&
  !c.description.trim() &&
  !c.html.trim()
const blankChatDraft = (c: Chat) => c.review === 'draft' && !hasWorkValues(c)

/** 입력이 없는 편집인지 확인한다. 엔티티 생성은 CRUD 서비스에서 처리한다. */
export const isBlankDraft = (kind: Kind, x: unknown) =>
  kind === 'world'
    ? blankWorldDraft(x as World)
    : kind === 'character'
      ? blankCharacterDraft(x as Character)
      : kind === 'status'
        ? blankStatusDraft(x as StatusBoard)
        : kind === 'command'
          ? blankCommandDraft(x as Command)
          : blankChatDraft(x as Chat)

const EMPTY: Data = {
  worlds: {},
  characters: {},
  chats: {},
  statuses: {},
  commands: {},
  quest: { talked: false, claimed: [] },
}

/** 앨범의 "장소 도달" 조건을 판정하려면 상태에 장소가 있어야 한다 — 앨범을 켜면 숨은 장소 항목을 넣는다 */
export const PLACE_FIELD = (): StatusField => ({
  id: newRowId(),
  key: '장소',
  type: 'text',
  initial: '',
  options: [],
  rule: '장면이 옮겨 가면 새 장소 이름으로 바꾼다',
  hidden: true,
})

/** 이 원본을 연결한 작품들 — 편집 화면 “작품 n개에서 쓰는 중”, 공개 작품 반영 확인 */
export const usersOf = (
  chats: Record<string, Chat>,
  kind: 'status' | 'command',
  id: string
) =>
  Object.values(chats).filter((c) =>
    kind === 'status' ? c.statusId === id : c.commandIds.includes(id)
  )

const clientStore = create<RenewStoreState>()((set, get) => {
  const commit = (next: Data) => {
    const owner = get().owner
    if (!owner) return
    set({ ...next, savedAt: null })
  }
  const data = (): Data => ({
    worlds: get().worlds,
    characters: get().characters,
    chats: get().chats,
    statuses: get().statuses,
    commands: get().commands,
    quest: get().quest,
  })
  return {
    ...EMPTY,
    owner: null,
    savedAt: null,
    setOwner: (owner) => {
      if (get().owner === owner) return
      set({ owner, ...EMPTY, savedAt: null })
    },
    hydrate: (next) => {
      const owner = get().owner
      if (!owner) return
      const characters = Object.fromEntries(
        Object.entries(next.characters).map(([id, character]) => {
          const habits = normalizeHabitIds(id, character.habits)
          return [
            id,
            habits === character.habits ? character : { ...character, habits },
          ]
        })
      )
      const data = { ...next, characters, quest: get().quest }
      set({ ...data, savedAt: null })
    },
    updateWorld: (id, patch) => {
      const cur = get().worlds[id]
      if (cur)
        commit({
          ...data(),
          worlds: {
            ...get().worlds,
            [id]: { ...cur, ...patch, updatedAt: Date.now() },
          },
        })
    },
    updateCharacter: (id, patch) => {
      const cur = get().characters[id]
      if (cur)
        commit({
          ...data(),
          characters: {
            ...get().characters,
            [id]: { ...cur, ...patch, updatedAt: Date.now() },
          },
        })
    },
    updateChat: (id, patch) => {
      const cur = get().chats[id]
      if (cur)
        commit({
          ...data(),
          chats: {
            ...get().chats,
            [id]: { ...cur, ...patch, updatedAt: Date.now() },
          },
        })
    },
    updateStatus: (id, patch) => {
      const cur = get().statuses[id]
      if (cur)
        commit({
          ...data(),
          statuses: {
            ...get().statuses,
            [id]: { ...cur, ...patch, updatedAt: Date.now() },
          },
        })
    },
    updateCommand: (id, patch) => {
      const cur = get().commands[id]
      if (cur)
        commit({
          ...data(),
          commands: {
            ...get().commands,
            [id]: { ...cur, ...patch, updatedAt: Date.now() },
          },
        })
    },
    remove: (kind, id) => {
      const d = data()
      if (kind === 'world') {
        const worlds = { ...d.worlds }
        delete worlds[id]
        // 세계관을 지우면 소속 캐릭터는 비소속으로, 작품의 세계관 연결도 푼다
        const characters = Object.fromEntries(
          Object.entries(d.characters).map(([k, c]) => [
            k,
            c.worldId === id
              ? { ...c, worldId: null, worldMode: 'none' as const }
              : c,
          ])
        )
        const chats = Object.fromEntries(
          Object.entries(d.chats).map(([k, c]) => [
            k,
            c.worldId === id ? { ...c, worldId: null } : c,
          ])
        )
        commit({ ...d, worlds, characters, chats })
      } else if (kind === 'character') {
        const characters = { ...d.characters }
        delete characters[id]
        // 작품에서는 빠지기만 한다 — 작품은 남고 캐릭터 칸이 비면 제출 검사에 걸린다
        const chats = Object.fromEntries(
          Object.entries(d.chats).map(([k, c]) => [
            k,
            {
              ...c,
              characterIds: c.characterIds.filter((x) => x !== id),
              lead:
                c.lead === id
                  ? c.characterIds.find((x) => x !== id) || null
                  : c.lead,
            },
          ])
        )
        commit({ ...d, characters, chats })
      } else if (kind === 'status') {
        const statuses = { ...d.statuses }
        delete statuses[id]
        const chats = Object.fromEntries(
          Object.entries(d.chats).map(([k, c]) => [
            k,
            c.statusId === id ? { ...c, statusId: null } : c,
          ])
        )
        commit({ ...d, statuses, chats })
      } else if (kind === 'command') {
        const commands = { ...d.commands }
        delete commands[id]
        const chats = Object.fromEntries(
          Object.entries(d.chats).map(([k, c]) => [
            k,
            c.commandIds.includes(id)
              ? { ...c, commandIds: c.commandIds.filter((x) => x !== id) }
              : c,
          ])
        )
        commit({ ...d, commands, chats })
      } else {
        const chats = { ...d.chats }
        delete chats[id]
        commit({ ...d, chats })
      }
    },
    setQuest: (patch) =>
      commit({ ...data(), quest: { ...get().quest, ...patch } }),
  }
})

export const RenewStoreSnapshotContext = createContext<{
  state: RenewStoreState
  readyKinds: readonly Kind[]
  scope?: string
} | null>(null)

export function useStudioServerSnapshot() {
  return useContext(RenewStoreSnapshotContext)
}

function useRenewSnapshot(): RenewStoreState
function useRenewSnapshot<T>(selector: (state: RenewStoreState) => T): T
function useRenewSnapshot<T = RenewStoreState>(
  selector?: (state: RenewStoreState) => T
): T | RenewStoreState {
  const snapshot = useStudioServerSnapshot()
  const select = selector ?? ((state: RenewStoreState) => state)
  return useSyncExternalStore(
    clientStore.subscribe,
    () => select(clientStore.getState()),
    () => select(snapshot?.state ?? clientStore.getInitialState())
  )
}

export const useRenewStore = Object.assign(useRenewSnapshot, {
  getState: clientStore.getState,
  getInitialState: clientStore.getInitialState,
  setState: clientStore.setState,
  subscribe: clientStore.subscribe,
})
